import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { getSeasonalRate, calculateSeasonalTotal, getSeasonBreakdown, getFlatRateForVilla } from '@/lib/seasonalPricing';
import VillaBookingCalendar from './VillaBookingCalendar';

interface BookingRow {
  check_in: string;
  check_out: string;
}

interface VillaBookingSectionProps {
  villaSlug: string;
}

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return toDateStr(dt);
}

function nightsBetween(checkIn: string, checkOut: string): number {
  const [y1, m1, d1] = checkIn.split('-').map(Number);
  const [y2, m2, d2] = checkOut.split('-').map(Number);
  const a = new Date(y1, m1 - 1, d1).getTime();
  const b = new Date(y2, m2 - 1, d2).getTime();
  return Math.round((b - a) / 86400000);
}

function hasBookedNight(start: string, end: string, booked: Set<string>): boolean {
  let d = start;
  while (d < end) {
    if (booked.has(d)) return true;
    d = addDays(d, 1);
  }
  return false;
}

export default function VillaBookingSection(props: VillaBookingSectionProps) {
  const { villaSlug } = props;
  const { t } = useTranslation();

  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [checkIn, setCheckIn] = useState<string | null>(null);
  const [checkOut, setCheckOut] = useState<string | null>(null);
  const [step, setStep] = useState<0 | 1>(0);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [today] = useState<string>(() => toDateStr(new Date()));
  const [viewMonth, setViewMonth] = useState<{ year: number; month: number }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const texts = useMemo(
    () => ({
      subtitle: t('booking_subtitle'),
      title: t('booking_title'),
      stepDates: t('booking_step_dates'),
      stepDetails: t('booking_step_details'),
      selectDatesTitle: t('booking_select_dates_title'),
      selectDatesSubtitle: t('booking_select_dates_subtitle'),
      pickCheckIn: t('booking_pick_checkin'),
      pickCheckOut: t('booking_pick_checkout'),
      checkInLabel: t('booking_check_in_label'),
      checkOutLabel: t('booking_check_out_label'),
      nightSingular: t('booking_night_singular'),
      nightPlural: t('booking_night_plural'),
      continue: t('booking_continue'),
      clear: t('booking_clear'),
      invalidRange: t('booking_invalid_range'),
      legendAvailable: t('booking_legend_available'),
      legendBooked: t('booking_legend_booked'),
      legendSelected: t('booking_legend_selected'),
      reviewTitle: t('booking_review_title'),
      reviewSubtitle: t('booking_review_subtitle'),
      nameLabel: t('booking_name_label'),
      namePlaceholder: t('booking_name_placeholder'),
      phoneLabel: t('booking_phone_label'),
      phonePlaceholder: t('booking_phone_placeholder'),
      emailLabel: t('booking_email_label'),
      emailPlaceholder: t('booking_email_placeholder'),
      notesLabel: t('booking_notes_label'),
      notesPlaceholder: t('booking_notes_placeholder'),
      submit: t('booking_submit'),
      submitting: t('booking_submitting'),
      requiredName: t('booking_required_name'),
      requiredPhone: t('booking_required_phone'),
      requiredEmail: t('booking_required_email'),
      invalidEmail: t('booking_invalid_email'),
      back: t('booking_back'),
      loading: t('booking_loading'),
      error: t('booking_error'),
      retry: t('booking_retry'),
      successTitle: t('booking_success_title'),
      successMessage: t('booking_success_message'),
      bookAnother: t('booking_book_another'),
      notConfirmed: t('booking_not_confirmed'),
      prevMonth: t('booking_prev_month'),
      nextMonth: t('booking_next_month'),
      weekdays: [
        t('booking_week_mon'),
        t('booking_week_tue'),
        t('booking_week_wed'),
        t('booking_week_thu'),
        t('booking_week_fri'),
        t('booking_week_sat'),
        t('booking_week_sun'),
      ],
      months: [
        t('booking_month_jan'),
        t('booking_month_feb'),
        t('booking_month_mar'),
        t('booking_month_apr'),
        t('booking_month_may'),
        t('booking_month_jun'),
        t('booking_month_jul'),
        t('booking_month_aug'),
        t('booking_month_sep'),
        t('booking_month_oct'),
        t('booking_month_nov'),
        t('booking_month_dec'),
      ],
    }),
    [t]
  );

  const bookedDates = useMemo(() => {
    const set = new Set<string>();
    bookings.forEach((b) => {
      let d = b.check_in;
      while (d < b.check_out) {
        set.add(d);
        d = addDays(d, 1);
      }
    });
    return set;
  }, [bookings]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select('check_in, check_out')
        .eq('villa_slug', villaSlug)
        .neq('status', 'cancelled');
      if (fetchError) throw fetchError;
      setBookings((data as BookingRow[]) || []);
    } catch {
      setError(texts.error);
    } finally {
      setLoading(false);
    }
  }, [villaSlug, texts.error]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  function handleSelectDate(dateStr: string) {
    if (dateStr < today) return;

    if (!checkIn || (checkIn && checkOut)) {
      if (bookedDates.has(dateStr)) return;
      setCheckIn(dateStr);
      setCheckOut(null);
      return;
    }

    if (dateStr <= checkIn) {
      if (bookedDates.has(dateStr)) return;
      setCheckIn(dateStr);
      setCheckOut(null);
      return;
    }

    if (hasBookedNight(checkIn, dateStr, bookedDates)) {
      if (bookedDates.has(dateStr)) return;
      setCheckIn(dateStr);
      setCheckOut(null);
      return;
    }

    setCheckOut(dateStr);
  }

  function changeMonth(delta: number) {
    setViewMonth((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }

  function handleClear() {
    setCheckIn(null);
    setCheckOut(null);
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!checkIn || !checkOut) return;
    if (!form.name.trim()) {
      setSubmitError(texts.requiredName);
      return;
    }
    if (!form.phone.trim()) {
      setSubmitError(texts.requiredPhone);
      return;
    }
    if (!form.email.trim()) {
      setSubmitError(texts.requiredEmail);
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      setSubmitError(texts.invalidEmail);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const { error: insertError } = await supabase.from('bookings').insert({
        villa_slug: villaSlug,
        check_in: checkIn,
        check_out: checkOut,
        guest_name: form.name.trim(),
        guest_phone: form.phone.trim(),
        guest_email: form.email.trim(),
        notes: form.notes.trim() || null,
        total_amount: totalCost,
        status: 'pending',
      });
      if (insertError) throw insertError;

      try {
        await supabase.functions.invoke('booking-notification', {
          body: {
            villaSlug,
            guestName: form.name.trim(),
            guestEmail: form.email.trim(),
            guestPhone: form.phone.trim(),
            checkIn,
            checkOut,
            nights,
            totalAmount: totalCost,
            notes: form.notes.trim(),
          },
        });
      } catch {
        // A notification failure should not block the booking request.
      }

      setSuccess(true);
      setForm({ name: '', phone: '', email: '', notes: '' });
      setCheckIn(null);
      setCheckOut(null);
      setStep(0);
      await fetchBookings();
    } catch {
      setSubmitError(texts.error);
    } finally {
      setSubmitting(false);
    }
  }

  function handleBookAnother() {
    setSuccess(false);
    setStep(0);
  }

  const nights = checkIn && checkOut ? nightsBetween(checkIn, checkOut) : 0;
  const nightsLabel = nights === 1 ? texts.nightSingular : texts.nightPlural;
  const totalCost = checkIn && checkOut ? calculateSeasonalTotal(checkIn, checkOut, villaSlug) : 0;
  const totalLabel = t('booking_total', 'Total');
  const avgRate = nights > 0 ? Math.round((totalCost / nights) * 100) / 100 : 0;
  const seasonInfo = checkIn && checkOut ? getSeasonBreakdown(checkIn, checkOut, villaSlug) : null;
  const flatRate = getFlatRateForVilla(villaSlug);

  return (
    <section id="booking" className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10 md:mb-12">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-foreground-500 font-medium mb-3">
            {texts.subtitle}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950">
            {texts.title}
          </h2>
        </div>

        {loading ? (
          <div className="py-16 flex flex-col items-center justify-center gap-3">
            <span className="w-8 h-8 border-2 border-background-300 border-t-primary-500 rounded-full animate-spin" />
            <p className="text-sm text-foreground-600">{texts.loading}</p>
          </div>
        ) : error ? (
          <div className="py-16 flex flex-col items-center justify-center gap-4">
            <p className="text-sm text-foreground-700">{error}</p>
            <button
              type="button"
              onClick={fetchBookings}
              className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              {texts.retry}
            </button>
          </div>
        ) : success ? (
          <div className="py-16 flex flex-col items-center justify-center gap-4 text-center">
            <span className="w-14 h-14 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center">
              <i className="ri-check-line text-2xl"></i>
            </span>
            <h3 className="font-heading text-2xl font-semibold text-foreground-950">
              {texts.successTitle}
            </h3>
            <p className="text-sm text-foreground-600 max-w-md">{texts.successMessage}</p>
            <button
              type="button"
              onClick={handleBookAnother}
              className="mt-2 bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-md text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              {texts.bookAnother}
            </button>
          </div>
        ) : (
          <div>
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-8">
              {[texts.stepDates, texts.stepDetails].map((label, i) => {
                const isActive = step === i;
                const isDone = step > i;
                return (
                  <div key={i} className="flex items-center gap-2 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold whitespace-nowrap ${
                          isActive || isDone
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-200 text-foreground-500'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span
                        className={`text-xs md:text-sm font-medium whitespace-nowrap ${
                          isActive ? 'text-foreground-950' : 'text-foreground-500'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {i < 1 && (
                      <div
                        className={`h-px flex-1 ${isDone ? 'bg-primary-500' : 'bg-background-300'}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {step === 0 ? (
              <div>
                <div className="text-center mb-6">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-1">
                    {texts.selectDatesTitle}
                  </h3>
                  <p className="text-sm text-foreground-600">{texts.selectDatesSubtitle}</p>
                  <p className="text-sm font-medium text-foreground-700 mt-2">
                    {!checkIn ? texts.pickCheckIn : !checkOut ? texts.pickCheckOut : ''}
                  </p>
                </div>

                {/* Selection summary */}
                <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
                  <span className="inline-flex items-center gap-2 bg-background-100 text-foreground-900 px-4 py-2 rounded-full text-sm font-medium">
                    <i className="ri-calendar-check-line text-base"></i>
                    {texts.checkInLabel}: {checkIn ?? '—'}
                  </span>
                  <span className="inline-flex items-center gap-2 bg-background-100 text-foreground-900 px-4 py-2 rounded-full text-sm font-medium">
                    <i className="ri-calendar-line text-base"></i>
                    {texts.checkOutLabel}: {checkOut ?? '—'}
                  </span>
                  {nights > 0 && (
                    <span className="inline-flex items-center gap-2 bg-accent-100 text-accent-900 px-4 py-2 rounded-full text-sm font-medium">
                      <i className="ri-moon-line text-base"></i>
                      {nights} {nightsLabel}
                    </span>
                  )}
                  {totalCost > 0 && (
                    <span className="inline-flex items-center gap-2 bg-primary-100 text-primary-900 px-4 py-2 rounded-full text-sm font-medium">
                      <i className="ri-money-euro-circle-line text-base"></i>
                      €{totalCost} total
                    </span>
                  )}
                </div>

                <div className="bg-background-100 rounded-lg p-4 md:p-6">
                  <VillaBookingCalendar
                    year={viewMonth.year}
                    month={viewMonth.month}
                    bookedDates={bookedDates}
                    checkIn={checkIn}
                    checkOut={checkOut}
                    today={today}
                    weekdays={texts.weekdays}
                    months={texts.months}
                    prevMonthLabel={texts.prevMonth}
                    nextMonthLabel={texts.nextMonth}
                    legendAvailable={texts.legendAvailable}
                    legendBooked={texts.legendBooked}
                    legendSelected={texts.legendSelected}
                    onSelectDate={handleSelectDate}
                    onChangeMonth={changeMonth}
                  />
                </div>

                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => checkIn && checkOut && setStep(1)}
                    disabled={!checkIn || !checkOut}
                    className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-white px-8 py-3 rounded-md text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
                  >
                    {texts.continue}
                  </button>
                  {(checkIn || checkOut) && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-circle-line text-base"></i>
                      {texts.clear}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="text-center mb-6">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-1">
                    {texts.reviewTitle}
                  </h3>
                  <p className="text-sm text-foreground-600">{texts.reviewSubtitle}</p>
                </div>

                <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-foreground-500 mb-1">
                        {texts.checkInLabel}
                      </p>
                      <p className="text-base font-semibold text-foreground-950">{checkIn}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-foreground-500 mb-1">
                        {texts.checkOutLabel}
                      </p>
                      <p className="text-base font-semibold text-foreground-950">{checkOut}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-foreground-500 mb-1">
                        {texts.nightPlural}
                      </p>
                      <p className="text-base font-semibold text-foreground-950">
                        {nights} {nightsLabel}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-foreground-500 mb-1">
                        {totalLabel}
                      </p>
                      <p className="text-base font-semibold text-foreground-950">
                        €{totalCost}
                      </p>
                    </div>
                  </div>
                  {seasonInfo && (
                    <div className="mt-3 pt-3 border-t border-background-200/70 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-600">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-information-line text-foreground-400"></i>
                        Avg. €{avgRate}/night
                      </span>
                      <span className="text-foreground-400">·</span>
                      <span>
                        {flatRate != null
                          ? `Flat rate €${flatRate}/night`
                          : seasonInfo.total === nights * 190
                            ? 'High season rate'
                            : seasonInfo.total === nights * 160
                              ? 'Low season rate'
                              : 'Mixed season rates'}
                      </span>
                    </div>
                  )}
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="villa-booking-name"
                        className="block text-sm font-medium text-foreground-900 mb-1.5"
                      >
                        {texts.nameLabel}
                      </label>
                      <input
                        id="villa-booking-name"
                        name="guest_name"
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                        placeholder={texts.namePlaceholder}
                        className="w-full px-4 py-2.5 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="villa-booking-phone"
                        className="block text-sm font-medium text-foreground-900 mb-1.5"
                      >
                        {texts.phoneLabel}
                      </label>
                      <input
                        id="villa-booking-phone"
                        name="guest_phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                        placeholder={texts.phonePlaceholder}
                        className="w-full px-4 py-2.5 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label
                      htmlFor="villa-booking-email"
                      className="block text-sm font-medium text-foreground-900 mb-1.5"
                    >
                      {texts.emailLabel}
                    </label>
                    <input
                      id="villa-booking-email"
                      name="guest_email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder={texts.emailPlaceholder}
                      className="w-full px-4 py-2.5 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>

                  <div className="mt-4">
                    <label
                      htmlFor="villa-booking-notes"
                      className="block text-sm font-medium text-foreground-900 mb-1.5"
                    >
                      {texts.notesLabel}
                    </label>
                    <textarea
                      id="villa-booking-notes"
                      name="notes"
                      rows={3}
                      maxLength={500}
                      value={form.notes}
                      onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                      placeholder={texts.notesPlaceholder}
                      className="w-full px-4 py-2.5 rounded-md text-sm bg-background-50 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                    />
                  </div>

                  {submitError && (
                    <p className="mt-3 text-sm text-accent-700">{submitError}</p>
                  )}

                  <div className="mt-5 flex items-start gap-2.5 bg-background-100 rounded-lg p-3.5">
                    <i className="ri-information-line text-lg text-foreground-500 mt-0.5"></i>
                    <p className="text-sm text-foreground-700">{texts.notConfirmed}</p>
                  </div>

                  <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-3.5 rounded-md text-base font-semibold whitespace-nowrap transition-colors cursor-pointer"
                    >
                      {submitting ? texts.submitting : texts.submit}
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(0)}
                      className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 px-4 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-base"></i>
                      {texts.back}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}